import { useRef, useState } from "react";
import styles from "./list.module.css";
import { Button, Dialog, Checkbox, TableRow, TableCell, TableBody, TableHead, Table, Paper, TableContainer } from "@mui/material";
import { ChevronDown, ChevronUp, Edit3, Plus } from "react-feather";
import { AddOutlined, FileDownloadOutlined, PersonAddAltOutlined, PrintOutlined } from "@mui/icons-material";
import Loader from "../../../components/Loader";
import { authData } from "../../../components/GetAuth";
import TablePaginationComponent from "../../../components/TablePagination";
import { StyledMainTableCell, StyledMainTableRow } from "../../../components/TableComponent";
import { ComponentToPrintList } from "./ComponentToPrintList";
import { useReactToPrint } from "react-to-print";
import AddDriverForm from "../../../components/popups/AddDriverForm";
import AssignDeliveryDriverPopup2 from "../../../components/popups/AssignDeliveryDriverPopup2";
import BookingItemsPanel, { eligibleItemIds } from "./BookingItemsPanel";
import ListToolbar from "../../../components/ListToolbar/ListToolbar";
import { downloadCsv } from "../../../components/ListToolbar/csv";

export const DELIVERY_EXPORT_COLUMNS = [
    { label: "Booking Id", value: (row) => row?.booking_id },
    { label: "Customer Name", value: (row) => row?.customer_name },
    { label: "Recipient Name", value: (row) => row?.recipient_details?.name },
    { label: "Delivery Address", value: (row) => row?.recipient_details?.address },
    { label: "Order Status", value: (row) => (parseInt(row?.payment_status) === 1 ? "Paid" : "Unpaid") },
    { label: "Contact No", value: (row) => row?.recipient_details?.phone },
    { label: "Container", value: (row) => row?.container_name },
];

const ListView = ({ filteredData, setPaymentModalOpen, filterVars, setFilterVars, selected, setSelectBooking, setNoteData, setNoteModalOpen, toggleItem, toggleBookingSelection, setSelected, setSnackbarMessage, setSnackbarOpen, setSnackbarType, refreshBookings, driverList, viewSwitch }) => {
    const printRef = useRef();
    const handlePrint = useReactToPrint({ content: () => printRef.current });
    const auth = authData();
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [driverFormOpen, setDriverFormOpen] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [expandedId, setExpandedId] = useState(null);

    const canAssign = auth?.user_type === 2 || auth?.permissions?.indexOf('delivery:assign-driver') > -1;

    const paginationChangePage = (event, newPage) => {
        setPage(newPage);
    }

    const paginationChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }

    const assignDrivers = () => {
        if (selected.length === 0) {
            setSnackbarType("error");
            setSnackbarOpen(true);
            setSnackbarMessage("Please select at least one item");

            return false;
        }
        setFormOpen(true);
    }

    const onAssign = () => {
        setSnackbarType("success");
        setSnackbarOpen(true);
        setSnackbarMessage("Driver assigned successfully.");

        setSelected([]);
        refreshBookings();
    }

    // Every eligible item across the whole (filtered) list — used to drive
    // the header "select all" checkbox.
    const allEligibleIds = filteredData?.flatMap((b) => eligibleItemIds(b)) || [];
    const allSelected = allEligibleIds.length > 0 && allEligibleIds.every((id) => selected.indexOf(id) > -1);
    const someSelected = allEligibleIds.some((id) => selected.indexOf(id) > -1);

    return (<>
        <Loader open={loading} setOpen={setLoading.bind(this)} />

        <ListToolbar
            search={filterVars?.searchKey}
            onSearch={(value) => setFilterVars((prev) => ({ ...prev, searchKey: value }))}
            dateRange={filterVars?.date}
            onDateRange={(value) => setFilterVars((prev) => ({ ...prev, date: value }))}
            viewSwitch={viewSwitch}
            selectedCount={selected.length}
            onClearSelection={() => setSelected([])}
            primaryAction={canAssign ? { label: selected.length ? `Assign to driver (${selected.length})` : "Assign to driver", icon: <PersonAddAltOutlined />, onClick: assignDrivers, disabled: selected.length === 0, hint: "Tick the items to deliver first" } : null}
            menuItems={[
                canAssign && { label: "Add driver", icon: <AddOutlined fontSize="small" />, onClick: () => setDriverFormOpen(true) },
                { label: "Export CSV", icon: <FileDownloadOutlined fontSize="small" />, onClick: () => downloadCsv("delivery-items.csv", DELIVERY_EXPORT_COLUMNS, filteredData), disabled: !filteredData?.length },
                { label: "Print bookings", icon: <PrintOutlined fontSize="small" />, onClick: handlePrint, disabled: !filteredData?.length },
            ]}
        />
        <div style={{ display: "none" }}>
            <ComponentToPrintList ref={printRef} data={filteredData} />
        </div>

        <div className={`${styles.CusTable}`}>
            <TableContainer component={Paper} className={styles.ResponsiveTableWrap}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            {canAssign && <StyledMainTableCell padding="checkbox">
                                <Checkbox
                                    className={`${styles.AllCheckbox}`}
                                    color="primary"
                                    indeterminate={someSelected && !allSelected}
                                    checked={allSelected}
                                    disabled={allEligibleIds.length === 0}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelected((prev) => Array.from(new Set([...prev, ...allEligibleIds])));
                                            return;
                                        }
                                        setSelected((prev) => prev.filter((id) => allEligibleIds.indexOf(id) === -1));
                                    }}
                                />
                            </StyledMainTableCell>}
                            <StyledMainTableCell>Booking Id</StyledMainTableCell>
                            <StyledMainTableCell>Customer Name</StyledMainTableCell>
                            <StyledMainTableCell>Recepient Name</StyledMainTableCell>
                            <StyledMainTableCell>Delivery Address</StyledMainTableCell>
                            <StyledMainTableCell>Delivery Notes</StyledMainTableCell>
                            <StyledMainTableCell>Payment Status</StyledMainTableCell>
                            <StyledMainTableCell>Items</StyledMainTableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => {
                            const eligibleIds = eligibleItemIds(item);
                            const bookingAllSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selected.indexOf(id) > -1);
                            const bookingSomeSelected = eligibleIds.some((id) => selected.indexOf(id) > -1);
                            const isExpanded = expandedId === item.id;
                            const totalItems = item?.products?.length || 0;

                            return (<>
                                <StyledMainTableRow
                                    key={item?.id}
                                    className={styles.ResponsiveRow}
                                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                >
                                    {canAssign && <TableCell padding="checkbox" className={styles.CheckboxCell}>
                                        <Checkbox
                                            color="primary"
                                            indeterminate={bookingSomeSelected && !bookingAllSelected}
                                            checked={bookingAllSelected}
                                            disabled={eligibleIds.length === 0}
                                            onChange={() => toggleBookingSelection(item)}
                                        />
                                    </TableCell>}
                                    <TableCell component="th" scope="row" data-label="Booking Id" className={styles.Mono}>{item?.booking_id}</TableCell>
                                    <TableCell component="th" scope="row" data-label="Customer Name">{item?.customer_name}</TableCell>
                                    <TableCell component="th" scope="row" data-label="Recepient Name">{item?.recipient_details?.name}</TableCell>
                                    <TableCell component="th" scope="row" data-label="Delivery Address">{item?.recipient_details?.address}</TableCell>
                                    <TableCell component="th" scope="row" data-label="Delivery Notes">{item?.delivery_note ? <><p>{item?.delivery_note}</p><button className={`${styles.AddNote}`} onClick={(e) => { e.preventDefault(); setNoteModalOpen(true); setNoteData(item?.delivery_note); setSelectBooking(item); }}><Edit3 size={12} /> Update</button></> : <button className={`${styles.AddNote}`} onClick={(e) => { e.preventDefault(); setNoteModalOpen(true); setNoteData(null); setSelectBooking(item); }}><Plus /> Add Note</button>}</TableCell>
                                    <TableCell data-label="Payment Status">
                                        {item?.payment_status === 1 || item?.payment_status === "1" ? (<Button className={`${styles.StatusBU} ${styles.DisaBU}`}>Paid</Button>) : (<Button className={`${styles.StatusBU}`} onClick={(e) => { setPaymentModalOpen(true); setSelectBooking(item); }}>Unpaid</Button>)}
                                    </TableCell>
                                    <TableCell data-label="Items">
                                        <button className={styles.ExpandToggle} onClick={(e) => setExpandedId(isExpanded ? null : item.id)}>
                                            <span className={styles.ItemCountBadge}>{eligibleIds.length}/{totalItems} ready</span>
                                            {isExpanded ? <ChevronUp /> : <ChevronDown />}
                                        </button>
                                    </TableCell>
                                </StyledMainTableRow>

                                {isExpanded && <TableRow className={styles.ItemPanelRow}>
                                    <TableCell colSpan={canAssign ? 8 : 7}>
                                        <BookingItemsPanel booking={item} selected={selected} onToggleItem={toggleItem} />
                                    </TableCell>
                                </TableRow>}
                            </>);
                        })}

                        {filteredData.length === 0 && (<TableRow>
                            <TableCell colSpan={10} align="center">No data found</TableCell>
                        </TableRow>)}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredData?.length > 0 && (<div className={styles.PaginationWrap}>
                <TablePaginationComponent
                    count={filteredData?.length}
                    colSpan={6}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleChangePage={paginationChangePage}
                    handleChangeRowsPerPage={paginationChangeRowsPerPage}
                />
            </div>)}
        </div>


        <Dialog open={formOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
            <AssignDeliveryDriverPopup2 setLoading={setLoading.bind(this)} modalClose={setFormOpen.bind(this)} itemIds={selected} onSubmit={onAssign.bind(this)} />
        </Dialog>

        <Dialog open={driverFormOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
            <AddDriverForm setFormOpen={setDriverFormOpen.bind(this)} setAllData={() => refreshBookings()} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setLoading={setLoading.bind(this)} editData={null} />
        </Dialog>
    </>);
};

export default ListView;